import { Attachment, Code, LinkOff } from '@mui/icons-material';
import { Alert, Box, Button, Divider, Stack, SxProps, Typography } from '@mui/material';
import { t } from 'i18next';
import { Interpolation } from '../../../config/i18n/components/Interpolation';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { EXTERNAL_URLS } from '../../../routing/routes';
import { FormEventHandler } from 'react';
import { useFormItem } from '../../../common/hooks/use-form-item';
import { InputType } from '../../../common/hooks/use-form-item/types';
import { maxLength } from '../../../common/hooks/use-form-item/validators/operational';
import { JIRA_ISSUE_TYPES } from '../../../organization/integrations/components/JiraProjectForm';
import { ButtonBox, PrimaryButton } from '../../../modal-flows/components/Buttons';
import { and } from '../../../common/hooks/use-form-item/validators/compositions';
import { script } from '../../../common/hooks/use-form-item/validators/type';

type Props = {
  isLoading: boolean;
  canEdit: boolean;
  issueCollectorCode: string;
  isJiraLinkEstablished: boolean;
  isIssueExportConfigured: boolean;
  jiraBaseUrl: string;
  jiraProjectKey: string;
  jiraIssueType: string;
  onIssueExportUnlink: () => void;
  onIssueCollectorUnlink: () => void;
  handleSave: (formCode: string, projectKey: string, issueType: string) => void;
  onTestIssueExport: (projectKey: string, issueType: string) => void;
};

export const STYLES = {
  title: { textTransform: 'uppercase', color: 'text.secondary', fontWeight: 500 },
} satisfies Record<string, SxProps>;

export const ProjectJiraForm = ({
  isLoading,
  canEdit,
  issueCollectorCode,
  onIssueCollectorUnlink,
  handleSave,
  isJiraLinkEstablished,
  isIssueExportConfigured,
  jiraBaseUrl,
  jiraProjectKey,
  jiraIssueType,
  onIssueExportUnlink,
  onTestIssueExport,
}: Props) => {
  const [_, baseUrlComponent] = useFormItem<string>({
    type: InputType.TYPE_TEXT,
    label: t('projectIntegrations.jiraIssueExport.baseUrlInputLabel'),
    value: jiraBaseUrl,
    validator: null,
    inputOptions: {
      disabled: true,
    },
  });
  const [projectKey, projectKeyComponent, isProjectKeyValid] = useFormItem<string>({
    type: InputType.TYPE_TEXT,
    label: t('projectIntegrations.jiraIssueExport.projectKeyInputLabel'),
    value: jiraProjectKey || '',
    validator: maxLength(1000),
    inputOptions: {
      disabled: isLoading || !canEdit,
    },
    textOptions: { trimOnBlur: true },
  });

  const [issueType, issueTypeComponent] = useFormItem<string>({
    type: InputType.TYPE_AUTOCOMPLETE,
    label: t('projectIntegrations.jiraIssueExport.issueTypeInputLabel'),
    value: jiraIssueType || '',
    validator: null,
    inputOptions: {
      disabled: isLoading || !canEdit,
    },
    autocompleteOptions: {
      readOnly: true,
      options: JIRA_ISSUE_TYPES,
    },
  });

  const [formCode, formCodeComponent, isFormCodeValid] = useFormItem({
    type: InputType.TYPE_TEXTAREA,
    inputOptions: {
      multiline: true,
      disabled: isLoading || !canEdit,
    },
    value: issueCollectorCode ?? '',
    validator: and(maxLength(1000), script()),
    label: t('projectIntegrations.jiraIssueCollector.inputLabel'),
  });

  // This boolean login ensures coverage of the following scenarios:
  // Save enabled if:
  // - issue collector field has changed and is valid/not empty
  // - either of the issue export fields have changed and are valid (both fields must have values)
  // Save disabled if:
  // - any field is cleared when it previously had a value
  // - a non valid value is entered in any field
  const isJiraIssueCollectorConfigured = !!issueCollectorCode;
  const issueCollectorChanged = formCode !== issueCollectorCode;
  const issueExportChanged = projectKey !== jiraProjectKey || issueType !== jiraIssueType;
  const canSubmitFormCode = (isFormCodeValid && !!formCode && issueCollectorChanged) || !issueCollectorChanged;
  const canSubmitIssueExport = (isProjectKeyValid && !!projectKey && !!issueType && issueExportChanged) || !issueExportChanged;
  const saveEnabled = canSubmitFormCode && canSubmitIssueExport && (issueCollectorChanged || issueExportChanged);

  const showIssueCollectorUnlink = isJiraIssueCollectorConfigured && canEdit;
  const showIssueExportUnlink = isIssueExportConfigured && canEdit;

  const handleJiraSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    handleSave(formCode, projectKey, issueType);
  };

  const handleTestClick = () => {
    onTestIssueExport(projectKey, issueType);
  };

  return (
    <>
      {!canEdit && <Alert severity="info">{t('projectIntegrations.noPermission')}</Alert>}
      <Stack sx={{ p: 3 }} spacing={2} component="form" onSubmit={handleJiraSubmit}>
        <Stack direction="column" gap={2}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Stack direction={'column'} spacing={1}>
              <Box sx={{ display: 'flex' }}>
                <Code sx={{ color: 'currentColor', mr: 1 }} />
                <Typography sx={{ textTransform: 'uppercase', color: 'text.secondary', fontWeight: 500 }}>
                  {t('projectIntegrations.jiraIssueCollector.title')}
                </Typography>
              </Box>
              <Interpolation
                i18nKey="projectIntegrations.jiraIssueCollector.learnMore"
                items={[
                  {
                    name: 'docs',
                    href: UrlHelper.wrapExternalDocsUrl(EXTERNAL_URLS.DOCS_JIRA_FORM_INTEGRATION),
                    'aria-label': t('a11y.project.integrations.docs.jiraFormIntegrationLabel'),
                  },
                ]}
                variant="body2"
              />
            </Stack>
            {showIssueCollectorUnlink && (
              <Button variant="outlined" color="error" sx={{ ml: 'auto', flexShrink: 0 }} onClick={onIssueCollectorUnlink}>
                <LinkOff sx={{ mr: 1 }} />
                {t('projectIntegrations.jiraIssueCollector.unlink')}
              </Button>
            )}
          </Box>
          {formCodeComponent}
        </Stack>
        <Stack direction="column" gap={2}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Stack direction={'column'} spacing={1}>
              <Box sx={{ display: 'flex' }}>
                <Attachment sx={{ color: 'currentColor', mr: 1 }} />
                <Typography sx={STYLES.title}>{t('projectIntegrations.jiraIssueExport.title')}</Typography>
              </Box>
              <Interpolation
                i18nKey="projectIntegrations.jiraIssueExport.learnMore"
                items={[
                  {
                    name: 'docs',
                    href: UrlHelper.wrapExternalDocsUrl(EXTERNAL_URLS.DOCS_JIRA_ISSUE_EXPORT),
                    'aria-label': t('a11y.project.integrations.docs.jiraIssueExportLabel'),
                  },
                ]}
                variant="body2"
              />
            </Stack>
            <Stack sx={{ ml: 'auto' }} gap={1} direction={'row'}>
              <Button variant="outlined" disabled={!canEdit || isLoading || !projectKey || !issueType} onClick={handleTestClick}>
                {t('projectIntegrations.jiraIssueExport.testConnection')}
              </Button>
              {showIssueExportUnlink && (
                <Button variant="outlined" color="error" sx={{ ml: 'auto', flexShrink: 0 }} onClick={onIssueExportUnlink}>
                  <LinkOff sx={{ mr: 1 }} />
                  {t('projectIntegrations.jiraIssueExport.unlink')}
                </Button>
              )}
            </Stack>
          </Box>
          {!isLoading && !isJiraLinkEstablished && (
            <Alert color="info" icon={false}>
              {t('projectIntegrations.jiraIssueExport.notConfigured')}
            </Alert>
          )}
          {isJiraLinkEstablished && (
            <Stack spacing={1} sx={{ maxWidth: '30rem' }}>
              {baseUrlComponent}
              {projectKeyComponent}
              {issueTypeComponent}
            </Stack>
          )}
        </Stack>
        <Divider />
        <ButtonBox>
          <PrimaryButton sx={{ ml: 'auto' }} disabled={!saveEnabled || !canEdit || isLoading} type="submit">
            {t('save')}
          </PrimaryButton>
        </ButtonBox>
      </Stack>
    </>
  );
};
